import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/stores/auth.store";

const INACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "pointerdown",
] as const;

/**
 * Locks the session after `timeoutMs` of inactivity.
 * Default: 15 minutes (900_000 ms).
 * Only active when the user is authenticated and not already locked.
 */
export function useInactivityLock(timeoutMs = 15 * 60 * 1000) {
  const navigate = useNavigate();
  const lock = useAuthStore((s) => s.lock);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLocked = useAuthStore((s) => s.isLocked);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      lock();
      navigate("/locked");
    }, timeoutMs);
  }, [lock, navigate, timeoutMs]);

  useEffect(() => {
    if (!isAuthenticated || isLocked) return;

    resetTimer();

    for (const event of INACTIVITY_EVENTS) {
      window.addEventListener(event, resetTimer, { passive: true });
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      for (const event of INACTIVITY_EVENTS) {
        window.removeEventListener(event, resetTimer);
      }
    };
  }, [isAuthenticated, isLocked, resetTimer]);
}
